import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyListWithImage } from "@/components/generic/multi-property-list-image";

import type { ApiError } from "@/data-interfaces/generics";
import type { Tool } from "@/data-interfaces/tools";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getRackToolDetailsProperties } from "@/lib/data-remappings/rack-tool-details";
import { getToolImage } from "@/lib/image-utils";
import { getBgColor, LogLevel } from "@/lib/log-level";
import {
	getAppWrappedPageUrl,
	RACK_TOOL_DETAILS_PAGE_URL,
	TOOLS_PAGE_URL,
} from "@/lib/page-routes";
import { TL_TOOL_URL } from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(RACK_TOOL_DETAILS_PAGE_URL),
)({
	component: RackToolDetails,
	validateSearch: (search: { tool: Tool; rack: string; position: number }) =>
		search,
});

function RackToolDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const searchParams = Route.useSearch();

	const [tool, setTool] = useState<Tool | ApiError>(searchParams.tool);

	useMQTTSubscribe(TL_TOOL_URL, setTool);

	useMQTTAutoRefreshPublish(30, TL_TOOL_URL, { tool_id: searchParams.tool.id });

	return (
		<div className="text-white p-8 flex flex-col flex-1">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type="button"
					onClick={() => {
						navigate({
							to: TOOLS_PAGE_URL,
							search: () => ({ selectedTab: "racks" }),
						});
					}}
				>
					{"< "}
					{t("Tools")}
				</button>
			</h2>

			<MqttSuspense
				objects={tool}
				requestUrl={TL_TOOL_URL}
				parameters={{ tool_id: searchParams.tool.id }}
				genericError="Error retriving tool"
			>
				{(tool) => (
					<MultiPropertyListWithImage
						multiPropertyObject={{
							properties: getRackToolDetailsProperties(
								tool,
								searchParams.rack,
								searchParams.position,
							),
						}}
						imageUrl={getToolImage(tool.type.id)}
						badges={[
							{
								label: "Worn",
								twColor: getBgColor(LogLevel.Warning),
								visible: tool.is_worn,
							},
							{
								label: "Broken",
								twColor: getBgColor(LogLevel.Error),
								visible: tool.is_broken,
							},
						]}
					/>
				)}
			</MqttSuspense>
		</div>
	);
}
